import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Grid, Button, Box, keyframes } from '@mui/material';
import MissionCard from './missionCard';
import MissionsMapView from './missionsMapView';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ApiClient from '../../../config/api';
import { useDispatch } from 'react-redux';
import { setIsLoading, setSnackBar } from '../../../store/globalSlice';
import getLocation, {
  getLocationCoords,
} from '../../../components/getLocation';

const normalizeSearch = (value) =>
  String(value || '')
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .trim();

const matchesMissionSearch = (item, searchQuery) => {
  const query = normalizeSearch(searchQuery);
  if (!query) return true;

  const searchableText = normalizeSearch(
    [
      item?.fullName,
      item?.phoneNumber,
      item?.address,
      item?.serviceType,
      item?.id,
      item?.count,
    ]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .join(' ')
  );

  return searchableText.includes(query);
};

const arrayMove = (array, from, to) => {
  const next = array.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const landIn = keyframes`
  0% {
    opacity: 0.55;
    transform: translateY(10px) scale(0.98);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const TabOne = ({
  data,
  setOpenDetail,
  setSelectedData,
  setMissionNo,
  setSelectedTask,
  setSelectedTaskDetail,
  location,
  getData,
  viewMode = 'list',
  searchQuery = '',
}) => {
  const api = ApiClient();
  const dispatch = useDispatch();
  const [items, setItems] = useState(data?.items || []);
  const [highlightId, setHighlightId] = useState(null);

  useEffect(() => {
    setItems(data?.items || []);
  }, [data?.items]);

  const visibleItems = useMemo(
    () =>
      items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => matchesMissionSearch(item, searchQuery)),
    [items, searchQuery]
  );
  const isSearching = Boolean(searchQuery.trim());

  const handleOptimizeRoute = async () => {
    try {
      dispatch(setIsLoading(true));
      const currentLocation = await getLocation();
      const coords = getLocationCoords(currentLocation) || { lat: 0, lng: 0 };

      await api.put('/api/app/v1/shipito/tasks/reorder-routes', {
        lat: coords.lat,
        lng: coords.lng,
      });
      getData();
    } catch (error) {
      console.error('Error Optimize:', error);
      dispatch(
        setSnackBar({
          message: 'چینش مجدد با خطا مواجه شد.',
          severity: 'error',
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const persistOrder = async (reordered) => {
    try {
      dispatch(setIsLoading(true));
      await api.put('/api/app/v1/shipito/tasks/manual-reorder', {
        items: reordered.map((item) => ({
          id: item.id,
          type: item?.type ?? 'Manifest',
        })),
      });
    } catch (error) {
      console.error('Error manual reorder:', error);
      dispatch(
        setSnackBar({
          message: 'تغییر ترتیب با خطا مواجه شد.',
          severity: 'error',
        })
      );
      getData();
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const moveItem = async (item, position) => {
    if (isSearching) return;

    const oldIndex = items.findIndex(
      (row) => String(row.id) === String(item.id)
    );
    if (oldIndex === -1) return;

    const newIndex = position === 'start' ? 0 : items.length - 1;
    if (oldIndex === newIndex) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    setHighlightId(item.id);
    window.setTimeout(() => setHighlightId(null), 700);

    await persistOrder(reordered);
  };

  if (viewMode === 'map') {
    return (
      <MissionsMapView
        items={visibleItems.map(({ item }) => item)}
        itemIndexes={Object.fromEntries(
          visibleItems.map(({ item, index }) => [item.id, index])
        )}
        emptyMessage={
          isSearching ? 'نتیجه‌ای یافت نشد' : 'ماموریتی ثبت نشده است'
        }
        setOpenDetail={setOpenDetail}
        setSelectedData={setSelectedData}
        setSelectedTask={setSelectedTask}
        setSelectedTaskDetail={setSelectedTaskDetail}
        setMissionNo={setMissionNo}
        getData={getData}
      />
    );
  }

  return (
    <Grid
      paddingRight={'20px'}
      paddingLeft={'20px'}
      container
      xs={12}
      flexDirection={'column'}
      alignItems={'center'}
    >
      {items?.length > 0 ? (
        <>
          <Grid container flexDirection="column">
            <Grid item flexGrow={1}>
              <Grid
                mb={2}
                item
                flexDirection="row"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography
                  paddingRight="20px"
                  paddingLeft="20px"
                  marginTop="20px"
                  variant="subtitle2"
                  textAlign="left"
                  sx={{
                    fontSize: '18px',
                  }}
                >
                  {isSearching
                    ? `تعداد : ${visibleItems.length} از ${items.length}`
                    : `تعداد کل : ${items.length || '-'}`}
                </Typography>
                <Button
                  size="large"
                  onClick={handleOptimizeRoute}
                  color="primary"
                  variant="contained"
                  sx={{
                    mt: '20px',
                    mr: '20px',
                    color: 'primary.contrastText',
                  }}
                >
                  چینش مجدد
                </Button>
              </Grid>
              {visibleItems.length > 0 ? (
                <Grid
                  item
                  flexDirection="column"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  {visibleItems.map(({ item, index }) => (
                    <Box
                      key={item.id}
                      sx={{
                        width: '100%',
                        animation:
                          highlightId === item.id
                            ? `${landIn} 0.55s cubic-bezier(0.22, 1, 0.36, 1)`
                            : 'none',
                      }}
                    >
                      <MissionCard
                        setSelectedData={setSelectedData}
                        item={item}
                        index={index}
                        setSelectedTaskDetail={setSelectedTaskDetail}
                        setOpenDetail={setOpenDetail}
                        setSelectedTask={setSelectedTask}
                        setMissionNo={setMissionNo}
                        canReorder={!isSearching}
                        isFirst={index === 0}
                        isLast={index === items.length - 1}
                        onMoveToStart={(mission) => moveItem(mission, 'start')}
                        onMoveToEnd={(mission) => moveItem(mission, 'end')}
                      />
                    </Box>
                  ))}
                </Grid>
              ) : (
                <Grid
                  item
                  flexDirection="column"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  height="200px"
                >
                  <AssignmentIcon sx={{ fontSize: '80px' }} color="disabled" />
                  <Typography variant="h6" color="textSecondary">
                    نتیجه‌ای یافت نشد
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid
          item
          flexDirection="column"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="200px"
        >
          <AssignmentIcon sx={{ fontSize: '80px' }} color="disabled" />
          <Typography variant="h6" color="textSecondary">
            ماموریتی ثبت نشده است
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default TabOne;
